import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { groupByMonth, monthLabel, sumStats, todayIso, yearRange } from '../../utils/dateBuckets';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { ChartTooltip, chartColors } from './ChartTooltip';
import { TrendStat } from './TrendStat';

export function MonthTrendView() {
  const { currentSiteId } = useAuth();
  const [year, setYear] = useState(() => Number(todayIso().slice(0, 4)));
  const range = yearRange(year);
  const prevRange = yearRange(year - 1);

  const current = useQuery({
    queryKey: ['daily-stats', currentSiteId, range.from, range.to],
    queryFn: () => api.dailyStats(currentSiteId!, range.from, range.to),
    enabled: !!currentSiteId,
  });
  const previous = useQuery({
    queryKey: ['daily-stats', currentSiteId, prevRange.from, prevRange.to],
    queryFn: () => api.dailyStats(currentSiteId!, prevRange.from, prevRange.to),
    enabled: !!currentSiteId,
  });

  const currentByMonth = new Map(groupByMonth(current.data?.days ?? []).map((b) => [b.month.slice(5, 7), b]));
  const previousByMonth = new Map(groupByMonth(previous.data?.days ?? []).map((b) => [b.month.slice(5, 7), b]));

  const chartData = Array.from({ length: 12 }, (_, i) => {
    const key = String(i + 1).padStart(2, '0');
    return {
      label: monthLabel(`${year}-${key}`),
      visitors: currentByMonth.get(key)?.visitors ?? 0,
      visitorsVorjahr: previousByMonth.get(key)?.visitors ?? 0,
      conversions: currentByMonth.get(key)?.conversions ?? 0,
    };
  });

  const totals = sumStats(current.data?.days ?? []);
  const prevTotals = sumStats(previous.data?.days ?? []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="trend-year-picker" className="text-sm text-slate-500 dark:text-slate-400">
          Jahr:
        </label>
        <input
          id="trend-year-picker"
          type="number"
          value={year}
          onChange={(e) => e.target.value && setYear(Number(e.target.value))}
          className="w-24 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>

      {current.isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <TrendStat label="Besucher (Jahr)" value={totals.visitors} previousValue={prevTotals.visitors} />
            <TrendStat label="Sessions (Jahr)" value={totals.sessions} previousValue={prevTotals.sessions} />
            <TrendStat label="Conversions (Jahr)" value={totals.conversions} previousValue={prevTotals.conversions} />
            <TrendStat label="Seitenaufrufe (Jahr)" value={totals.pageViews} previousValue={prevTotals.pageViews} />
          </div>

          <Card title={`Monatsverlauf ${year} vs. ${year - 1}`}>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="label" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#94a3b8' }} />
                <Bar dataKey="visitorsVorjahr" name={`Besucher ${year - 1}`} fill={chartColors.previous} radius={[4, 4, 0, 0]} />
                <Bar dataKey="visitors" name={`Besucher ${year}`} fill={chartColors.visitors} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}
    </div>
  );
}
